import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { BookmarkFolder, Bookmark } from '@ext-bookmark/shared/types';
import BookmarkCard from './BookmarkCard';
import BookmarkForm from './BookmarkForm';

interface BookmarkContentProps {
  folder: BookmarkFolder | null;
  subfolders: BookmarkFolder[];
  bookmarks: Bookmark[];
  bookmarksLoading?: boolean;
  onAddBookmark: (params: { name: string; url: string; note?: string }) => void;
  onEditBookmark: (bookmark: Bookmark) => void;
  onDeleteBookmark: (id: string) => void;
  onAddSubfolder: (name: string) => void;
  onBackup?: () => void;
  onRestore?: () => void;
  onInstallExtension?: () => void;
  duplicateError?: string | null;
  onClearDuplicateError?: () => void;
}

const BookmarkContent: React.FC<BookmarkContentProps> = ({
  folder,
  subfolders,
  bookmarks,
  bookmarksLoading = false,
  onAddBookmark,
  onEditBookmark,
  onDeleteBookmark,
  onAddSubfolder,
  onBackup,
  onRestore,
  onInstallExtension,
  duplicateError,
  onClearDuplicateError,
}) => {
  const navigate = useNavigate();
  const [showBookmarkForm, setShowBookmarkForm] = useState(false);
  const [showFolderForm, setShowFolderForm] = useState(false);

  const title = folder?.name ?? '书签栏';
  const isRoot = folder === null;
  const canAddBookmark = !isRoot;

  return (
    <div className="flex-1 flex flex-col min-w-0 bg-gray-50 dark:bg-gray-900">
      {/* 顶栏：返回、标题、搜索、新增 */}
      <div className="flex-shrink-0 flex items-center gap-3 p-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        {!isRoot && (
          <button
            type="button"
            onClick={() => navigate('/bookmarks')}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 min-h-[44px] min-w-[44px] touch-manipulation"
            aria-label="返回"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        <h1 className="flex-1 text-lg font-semibold text-gray-900 dark:text-white truncate">{title}</h1>
        <button
          type="button"
          onClick={() => setShowBookmarkForm(true)}
          disabled={!canAddBookmark}
          className="px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium min-h-[44px] touch-manipulation"
        >
          新增书签
        </button>
        <button
          type="button"
          onClick={() => setShowFolderForm(true)}
          className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm min-h-[44px] touch-manipulation"
        >
          新建文件夹
        </button>
        {onBackup && (
          <button
            type="button"
            onClick={onBackup}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm min-h-[44px] touch-manipulation"
            title="从浏览器备份书签到云端"
          >
            备份
          </button>
        )}
        {onRestore && (
          <button
            type="button"
            onClick={onRestore}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm min-h-[44px] touch-manipulation"
            title="从云端还原书签到浏览器"
          >
            还原
          </button>
        )}
        {onInstallExtension && (
          <button
            type="button"
            onClick={onInstallExtension}
            className="px-3 py-2 rounded-lg border border-dashed border-gray-400 dark:border-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm min-h-[44px] touch-manipulation"
            title="安装浏览器扩展"
          >
            安装扩展
          </button>
        )}
      </div>

      {duplicateError && (
        <div className="mx-3 mt-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-sm flex items-center justify-between">
          <span>{duplicateError}</span>
          {onClearDuplicateError && (
            <button type="button" onClick={onClearDuplicateError} className="underline">
              关闭
            </button>
          )}
        </div>
      )}

      {/* 内容区 */}
      <div className="flex-1 overflow-y-auto p-4">
        {subfolders.length > 0 && (
          <section className="mb-6">
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">文件夹</h2>
            <div className="flex flex-wrap gap-3">
              {subfolders.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => navigate(`/bookmarks/${f.id}`)}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md transition-shadow min-h-[44px] touch-manipulation cursor-pointer"
                >
                  <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                  </svg>
                  <span className="font-medium text-gray-900 dark:text-white">{f.name}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        <section>
          <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">书签</h2>
          {bookmarksLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : bookmarks.length === 0 && subfolders.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              当前文件夹为空，点击「新增书签」或「新建文件夹」开始添加
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {bookmarks.map((b) => (
                <BookmarkCard
                  key={b.id}
                  bookmark={b}
                  onEdit={onEditBookmark}
                  onDelete={onDeleteBookmark}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {/* 新增书签弹层 */}
      {showBookmarkForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-4">
            <h3 className="text-lg font-semibold mb-4">新增书签</h3>
            <BookmarkForm
              onSave={(data) => {
                onAddBookmark(data);
                setShowBookmarkForm(false);
              }}
              onCancel={() => setShowBookmarkForm(false)}
            />
          </div>
        </div>
      )}

      {/* 新建子文件夹 */}
      {showFolderForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-4">
            <h3 className="text-lg font-semibold mb-4">新建文件夹</h3>
            <FolderNameForm
              onSave={(name) => {
                onAddSubfolder(name);
                setShowFolderForm(false);
              }}
              onCancel={() => setShowFolderForm(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// 简单文件夹名称表单
const FolderNameForm: React.FC<{
  onSave: (name: string) => void;
  onCancel: () => void;
}> = ({ onSave, onCancel }) => {
  const [name, setName] = useState('');
  return (
    <div className="space-y-4">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="文件夹名称"
        className="input-field w-full"
        autoFocus
      />
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="btn-secondary">
          取消
        </button>
        <button
          type="button"
          onClick={() => name.trim() && onSave(name.trim())}
          className="btn-primary"
        >
          创建
        </button>
      </div>
    </div>
  );
};

export default BookmarkContent;
